import { Controller } from '@hotwired/stimulus'
import * as Turbo from '@hotwired/turbo'

export default class extends Controller {
  static targets = ['form']
  static values = { url: String, blankParams: { type: Array, default: [] } }

  declare readonly formTarget: HTMLFormElement
  declare readonly hasUrlValue: boolean
  declare readonly urlValue: string
  declare readonly blankParamsValue: string[]

  connect (): void {
    this.formTarget.addEventListener('reset', this.resetForm.bind(this))
  }

  disconnect (): void {
    this.formTarget.removeEventListener('reset', this.resetForm.bind(this))
  }

  submitForm (event: Event): void {
    event.preventDefault()

    const form = this.formTarget
    const formData = new FormData(form)

    const rawDateRange = formData.get('q[created_at_range]')
    const dateRange = typeof rawDateRange === 'string' ? rawDateRange : ''

    if (dateRange !== '' && !dateRange.includes(' to ')) {
      const formattedRange = `${dateRange} to ${dateRange}`
      formData.set('q[created_at_range]', formattedRange)
    }

    this.visit((params: URLSearchParams): URLSearchParams => {
      for (const [key, value] of formData.entries()) {
        const stringValue = String(value)

        if (stringValue == null || stringValue === '') {
          params.delete(key)
          if (this.blankParamsValue.includes(key)) params.append(key, '')
          continue
        }

        if (key === 'q[created_at_range]') {
          params.delete(key)
          params.set(key, stringValue)
          continue
        }

        if (params.has(key)) {
          const existingValues = params.getAll(key)
          if (!existingValues.includes(stringValue)) {
            params.append(key, stringValue)
          }
        } else {
          params.set(key, stringValue)
        }
      }

      // NOTE: reset the page to 1
      params.delete('page')

      return params
    })

    const newEvent = new CustomEvent('count-checkboxes:update')
    document.dispatchEvent(newEvent)
  }

  resetDomainFilter (): void {
    this.formTarget.querySelectorAll("[name='q[domain_id_in][]']").forEach(checkbox => {
      (checkbox as HTMLInputElement).checked = false
    })

    const event = new CustomEvent('count-checkboxes:update')
    document.dispatchEvent(event)
  }

  resetForm (): void {
    this.clearAllFields()

    this.visit((params: URLSearchParams): URLSearchParams => {
      const formElements = Array.from(this.formTarget.elements) as HTMLInputElement[]

      formElements.forEach(element => {
        const fieldName = element.name
        if (fieldName === '') return

        const ignoreFlag = element.attributes.getNamedItem('data-ignore-reset')?.value
        if (ignoreFlag === 'true') return

        const dateInput = this.formTarget.querySelector<HTMLInputElement>('[name="q[created_at_range]"]')
        const defaultValue = dateInput?.dataset.defaultValue
        if (fieldName === 'q[created_at_range]' && defaultValue !== undefined && defaultValue !== null) {
          params.set(fieldName, defaultValue)
        } else {
          params.delete(fieldName)
        }
      })

      this.blankParamsValue.forEach(key => params.append(key, ''))

      return params
    })

    const datepickerEvent = new CustomEvent('datepicker:update')
    document.dispatchEvent(datepickerEvent)

    const event = new CustomEvent('count-checkboxes:update')
    document.dispatchEvent(event)
  }

  private visit (cb: Function): void {
    const basePath = this.hasUrlValue ? this.urlValue : window.location.pathname
    const currentSearchParams = this.hasUrlValue
      ? new URLSearchParams()
      : new URLSearchParams(window.location.search)
    const params = cb(currentSearchParams)
    const url = `${basePath}?${params as string}${window.location.hash}`

    if (!this.hasUrlValue) {
      Turbo.navigator.history.push(new URL(url, location.origin))
    }

    Turbo.visit(url, { frame: this.formTarget.dataset.turboFrame })
  }

  private clearAllFields (): void {
    Array.from(this.formTarget.elements).forEach(element => {
      const input = (element as HTMLInputElement)
      const ignoreFlag = input.attributes.getNamedItem('data-ignore-reset')?.value

      if (ignoreFlag === 'true') return

      const defaultValue = input.dataset.defaultValue

      if (input.name === 'q[created_at_range]' && typeof defaultValue === 'string') {
        input.value = defaultValue
      } else {
        switch (input.type) {
          case 'search':
            input.value = ''
            break
          case 'checkbox':
            input.checked = false
            break
        }
      }
    })
  }
}
